import React, { useEffect } from 'react'
import styled from 'styled-components'
import { rem, transparentize } from 'polished'
import { useIntl, FormattedMessage } from 'react-intl'
import { mediaQueryUp } from 'components/utils/breakpoint'
import PaletteSheet from 'LuxKit/PaletteSheet'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import NotAvailablePlaceholder from 'loyalty/components/NotAvailablePlaceholder'
import checkPersonalisedSaving from 'luxPlus/lib/checkPersonalisedSaving'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchMembershipMetrics } from 'actions/LuxPlusActions'
import useRebrandVersion from 'hooks/useRebrandVersion'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  margin: 0 auto;
`

const StatCardWrapper = styled(PaletteSheet)`
  border-radius: ${props => props.theme.borderRadius.L};
  box-shadow: 0 ${rem(17)} ${rem(29)} ${transparentize(0.7, '#000000')};
  overflow: hidden;
  width: ${rem(250)};

  ${mediaQueryUp.tablet} {
    width: ${rem(280)};
  }
`

const StatCardWrapperBlue = styled(StatCardWrapper)`
  border: 1px solid ${props => props.theme.palette.highlight.primary.lightForeground};
  position: relative;
  z-index: 1;
  max-width: ${rem(204)};
  margin-top: ${rem(-20)};
  margin-inline-start: ${rem(100)};

  ${mediaQueryUp.tablet} {
    width: ${rem(184)};
    margin-inline-start: ${rem(120)};
  }

  ${mediaQueryUp.desktop} {
    margin-inline-start: ${rem(170)};
  }
`

const StatCardContentWhite = styled.div`
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding: ${rem(40)} ${rem(20)};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${rem(4)};
  align-items: center;
  min-height: ${rem(188)};
`

const StatCardContentBlue = styled.div`
  background: ${props => props.theme.palette.product.luxPlus.background};
  padding: ${rem(40)} ${rem(20)};
  text-align: center;
`

const ClickableUnderline = styled.button`
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
`

interface Props {
  saveAmount: number
  firstName?: string
  personalSaving?: number
  onPastBookingsClick?: () => void
  hasFlightSavings?: boolean
  loading?: boolean
}

function LuxPlusStatCards({ saveAmount, firstName, personalSaving, onPastBookingsClick, hasFlightSavings, loading }: Props) {
  const accentColour = useRebrandSuccessDarkColour()
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const isRebrand = useRebrandVersion() === 'rebrand'
  const trustedByAccentColour = isRebrand ? 'lux-plus-contrast' : 'highlight-primary-dark'
  const saving = checkPersonalisedSaving(firstName, personalSaving)
  const showPersonalisedCard = saving.showPersonalised || (!!firstName && !!hasFlightSavings)

  const membershipMetrics = useAppSelector(state => state.luxPlus.membershipMetrics)
  const trustedByCount = membershipMetrics.data?.trustedByCount
  const isTrustedByLoading = (membershipMetrics.initial || membershipMetrics.fetching)
  const isAustralianRegion = useAppSelector(state => state.geo.currentRegionCode === 'AU')
  useEffect(() => {
    dispatch(fetchMembershipMetrics())
  }, [dispatch])

  const couldHaveSavedLabel = intl.formatMessage(
    {
      id: 'mIL_2zZ9moPwGAuwgsCG',
      defaultMessage: '{firstName}, you could have',
      description: 'Personalised heading telling the user they could have saved with LuxPlus+',
    },
    { firstName },
  )

  const withProgramNameLabel = intl.formatMessage(
    {
      id: 'PcPeHmWN1g3MJCFV6fDp',
      defaultMessage: 'with {programName}',
      description: 'Suffix indicating savings are with the LuxPlus+ program',
    },
    { programName: LUXURY_PLUS.PROGRAM_NAME },
  )

  const mostMembersLabel = intl.formatMessage(
    {
      id: '0VU6SCBkDEwVFw9zhSry',
      defaultMessage: '{programName} members can',
      description: 'Introduction text for general LuxPlus+ savings statistic',
    },
    { programName: LUXURY_PLUS.PROGRAM_NAME },
  )

  const eachYearLabel = intl.formatMessage({
    id: 'cIBX1WP_EzmpL7otC3_e',
    defaultMessage: 'each year*',
    description: 'Suffix for annual savings amount with asterisk disclaimer',
  })

  const trustedByLabel = intl.formatMessage({
    id: 'Qov_WuLVbN3WxPI_AimG',
    defaultMessage: 'Trusted by more than',
    description: 'Heading text for the traveller count statistic card on LuxPlus page',
  })

  const travellersLabel = isAustralianRegion
    ? intl.formatMessage({
        id: 'NPAsU_SnHiMOWEeqc_aN',
        defaultMessage: 'Australians',
        description: 'Label below the traveller count statistic for Australian users',
      })
    : intl.formatMessage({
        id: '2Z-PMociLhzvJMlsExsB',
        defaultMessage: 'travellers',
        description: 'Label below the traveller count statistic for non-AU users',
      })

  return (
    <CardsContainer>
      <StatCardWrapper paletteType="default">
        <StatCardContentWhite>
          {loading && <>
            <TextLoadingBox width="18ch" typography="body-medium" />
            <TextLoadingBox width="10ch" typography="heading3" />
            <TextLoadingBox width="12ch" typography="heading3" />
          </>}
          {!loading && showPersonalisedCard && saving.showPersonalised && <>
            <BodyText variant="large" weight="bold" colour="light-foreground-alt">
              {couldHaveSavedLabel}
            </BodyText>
            <Heading variant="heading3" colour={accentColour}>
              <FormattedMessage
                id="UfATKFH8b4GhjwYV5p0x"
                defaultMessage="saved at least {amount}"
                description="Personal savings amount the user could have saved on past bookings"
                values={{
                  amount: <FormatCurrency value={saving.personalSaving} format="rounded" />,
                }}
              />
            </Heading>
            <BodyText variant="medium" weight="bold" colour="light-foreground-alt">
              <FormattedMessage
                id="NYGTprgC3hF9v0y0fjW1"
                defaultMessage="on <pastBookings>past bookings</pastBookings>"
                description="Link to view past bookings where savings could have applied"
                values={{
                  pastBookings: (chunks: React.ReactNode) => <ClickableUnderline type="button" onClick={onPastBookingsClick}>{chunks}</ClickableUnderline>,
                }}
              />
            </BodyText>
            <BodyText variant="large" weight="bold" colour="light-foreground-alt">
              {withProgramNameLabel}
            </BodyText>
          </>}
          {!loading && showPersonalisedCard && !saving.showPersonalised && <BodyText variant="large" weight="bold" colour="light-foreground-alt">
            <FormattedMessage
              id="Nhb_XQe2r4TA_aMNyDdR"
              defaultMessage="{firstName}, you could have saved on <pastBookings>past bookings</pastBookings> with {programName}"
              description="Amount-less past-savings tile shown when the only past savings are flights (amount hidden)"
              values={{
                firstName,
                programName: LUXURY_PLUS.PROGRAM_NAME,
                pastBookings: (chunks: React.ReactNode) => <ClickableUnderline type="button" onClick={onPastBookingsClick}>{chunks}</ClickableUnderline>,
              }}
            />
          </BodyText>}
          {!loading && !showPersonalisedCard && <>
            <BodyText variant="medium" weight="bold" colour="light-foreground-alt">
              {mostMembersLabel}
            </BodyText>
            <Heading variant="heading3" colour={accentColour}>
              <FormattedMessage
                id="SPvRRh7gRfsH5mZN-6SV"
                defaultMessage="save over {amount}"
                description="General savings amount most LuxPlus+ members save each year"
                values={{
                  amount: <FormatCurrency value={saveAmount} format="rounded" />,
                }}
              />
            </Heading>
            <Heading variant="heading3" colour={accentColour}>{eachYearLabel}</Heading>
          </>}
        </StatCardContentWhite>
      </StatCardWrapper>

      <StatCardWrapperBlue paletteType="inverse">
        <StatCardContentBlue>
          <BodyText weight="bold" variant="medium" colour="normal-contrast">{trustedByLabel}</BodyText>
          <Heading variant="heading3" colour={trustedByAccentColour}>
            {isTrustedByLoading && <TextLoadingBox width="7ch" typography="heading3" style={{ opacity: 0.3 }} />}
            {!isTrustedByLoading && trustedByCount !== undefined && <FormatDecimal value={trustedByCount} /> }
            {!isTrustedByLoading && trustedByCount === undefined && <NotAvailablePlaceholder />}
          </Heading>
          <Heading variant="heading3" colour={trustedByAccentColour}>{travellersLabel}</Heading>
        </StatCardContentBlue>
      </StatCardWrapperBlue>
    </CardsContainer>
  )
}

export default LuxPlusStatCards
